<template>
    <b-overlay
        class="h-100"
        :show="status === 'loading' && !editMode"
    >
        <div class="card-header align-items-start">
            <div class="card-group-title">
                <h4>
                    {{ $t('org.dashboard.Total Cost') }}
                </h4>
            </div>
        </div>
        <div class="card-body">
            <div
                class="text-center centered-content-totalcost d-flex"
                :class="{ 'flex-column': needResponsiveStyle }"
                style="font-size: 13px;"
            >
                <div
                    :class="{ 'content-border-right': !needResponsiveStyle, 'content-border-bottom my-2': needResponsiveStyle }"
                    class="w-100 flex-grow-1"
                >
                    <span>{{ $t('org.dashboard.PreviousMonth') }} <br v-if="needResponsiveStyle"/>({{ lastMonthTitle }})</span>
                    <p
                        class="mt-50"
                        style="font-size: 26px;"
                    >
                        {{ textData.previousMonth.cost }}
                    </p>
                </div>
                <div
                    :class="{ 'content-border-right': estimatable && !needResponsiveStyle, 'content-border-bottom': estimatable && needResponsiveStyle,'my-2': needResponsiveStyle }"
                    class="w-100 flex-grow-1"
                >
                    <span class="text-primary" style="font-weight: 700;">{{ $t('org.dashboard.ThisMonth') }} <br v-if="needResponsiveStyle"/>({{ thisMonthTitle }})</span>
                    <p
                        class="my-50 text-primary"
                        style="font-size: 26px; font-weight: 700;"
                    >
                        {{ textData.thisMonth.cost }}
                    </p>
                    <div
                        class="d-flex justify-content-center mt-1"
                        :class="{
                            'text-danger' : textData.thisMonth.rate > 0,
                            'text-success': textData.thisMonth.rate < 0,
                        }"
                    >
                        <feather-icon
                            v-if="Math.abs(textData.thisMonth.rate) > 0"
                            :icon="textData.thisMonth.rate >= 0 ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                            style="width: 22px; height: 18px; stroke-width: 2"
                        />
                        <span>{{ typeof textData.thisMonth.rate === 'number' ? $_getRatio(Math.abs(textData.thisMonth.rate)) : '- %' }}</span>
                    </div>
                    <span style="color: #959595;">{{ $t('org.dashboard.Compared to previous month') }}</span>
                </div>
                <div
                    v-if="estimatable"
                    class="w-100 flex-grow-1"
                    :class="{ 'my-2': needResponsiveStyle }"
                >
                    <span style="font-weight: 700; color: #A59DFF;">{{ $t('org.dashboard.EstimatedCost') }} <br v-if="needResponsiveStyle"/>({{ thisMonthTitle }})</span>
                    <p
                        class="my-50"
                        style="font-size: 26px; font-weight: 700; color: #A59DFF;"
                    >
                        {{ textData.estimatedCost.cost }}
                    </p>
                    <div
                        class="d-flex justify-content-center mt-1"
                        :class="{
                            'text-danger' : textData.estimatedCost.rate > 0,
                            'text-success': textData.estimatedCost.rate < 0,
                        }"
                    >
                        <feather-icon
                            v-if="Math.abs(textData.estimatedCost.rate) > 0"
                            :icon="textData.estimatedCost.rate >= 0 ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                            style="width: 22px; height: 18px; stroke-width: 2"
                        />
                        <span>{{ typeof textData.estimatedCost.rate === 'number' ? $_getRatio(Math.abs(textData.estimatedCost.rate)) : '- %' }}</span>
                    </div>
                    <span style="color: #959595;">{{ $t('org.dashboard.Compared to previous month') }}</span>
                </div>
            </div>
        </div>
    </b-overlay>
</template>
<script>
import { getCurrentInstance, computed, toRefs, watch } from '@vue/composition-api'
import useSelectMonth from '@/composables/useSelectMonth'
import useCommonData from '@/composables/useCommonData'
import useDashboard from '@/composables/useDashboard'
import moment from 'moment'

export default {
    props: ['editMode'],
    setup (props, context) {
        const vm = getCurrentInstance().proxy
        const store = vm.$store
        const { editMode } = toRefs(props)
        const { estimatable } = useSelectMonth()
        const { __toKDollarFormat } = useCommonData()
        const currentParams = computed(() => store.getters['$_dashboard/currentParams'])
        const { getTotalCost } = useDashboard()
        const [status, data] = getTotalCost(currentParams.value)
        const breakPoint = computed(() => store.state['app']['windowWidth'])
        const needResponsiveStyle = computed(() => breakPoint.value <= 576)

        const textData = computed(() => {
            const monthIncreasedRate = estimatable && data.value.cost && data.value.lastMonthCost ? (data.value.cost - data.value.lastMonthCost) / data.value.lastMonthCost * 100 : data.value.monthIncreasedRate
            return {
                previousMonth: {
                    cost: typeof data.value.lastMonthCost === 'number' ? __toKDollarFormat(data.value.lastMonthCost) : '-'
                },
                thisMonth: {
                    cost: typeof data.value.cost === 'number' ? __toKDollarFormat(data.value.cost) : '-',
                    rate: typeof monthIncreasedRate === 'number' ? monthIncreasedRate : '-'
                },
                estimatedCost: {
                    cost: estimatable && typeof data.value.estimatedCost === 'number' ? __toKDollarFormat(data.value.estimatedCost) : '-',
                    rate: estimatable && data.value.estimatedIncreasedRate
                }
            }
        })
        const duration = moment.duration({ 'months': -1 })
        const lastMonthTitle = computed(() => moment(thisMonthTitle.value, 'YYYY.MM').add(duration).format('YYYY.MM') !== 'Invalid date' ? moment(thisMonthTitle.value, 'YYYY.MM').add(duration).format('YYYY.MM') : '-')
        const thisMonthTitle = computed(() => moment(data.value.date, 'YYYY-MM-DD').format('YYYY.MM') !== 'Invalid date' ? moment(data.value.date, 'YYYY-MM-DD').format('YYYY.MM') : '-')
        watch(currentParams, (value, oldValue) => {
            getTotalCost(value)
        }, {
            deep: true
        })
        return {
            status,
            estimatable: editMode.value ? true : estimatable,
            textData,
            needResponsiveStyle,
            lastMonthTitle,
            thisMonthTitle
        }
    }
}
</script>
<style>
/* .flex-custom {
    display: flex;
    flex: 0 1;
}
.flex-custom {
    display: flex;
    flex: 0 1;
} */
.content-border-right,
.content-border-bottom {
    position: relative;
}
.content-border-right::after {
    content: '';
    display: inline-block;
    position: absolute;
    width: 1px;
    height: 88px;
    /* background: #C8C8C8; */
    border-right: 1px solid #C8C8C8;
    top: -5px;
    right: 0;
}
.content-border-bottom::after {
    content: '';
    display: inline-block;
    position: absolute;
    width: 70%;
    height: 1px;
    bottom: -18%;
    left: 50%;
    transform: translateX(calc(-50%));
    border-bottom: 1px solid #C8C8C8;
}
.centered-content-totalcost {
    position: absolute;
    width: 100%;
    top: 50%;
    left: 0rem;
    transform: translate(0, calc(-50% + 15px));
}
</style>
